import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Reveal } from "@/hooks/useReveal";
import { Play, ChevronDown } from "lucide-react";

// Extract a YouTube video ID from any common URL format.
const extractYouTubeId = (url) => {
  if (!url) return "";
  try {
    const u = new URL(url.trim());
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtu.be") return u.pathname.replace(/^\//, "").split("/")[0] || "";
    if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
      const v = u.searchParams.get("v");
      if (v) return v;
      const m = u.pathname.match(/^\/(?:embed|shorts|live|v)\/([A-Za-z0-9_-]+)/);
      if (m) return m[1];
    }
  } catch { /* noop */ }
  return "";
};

const QUARTER_LABELS = ["Q1", "Q2", "Q3", "Q4"];

// Single-quarter card. Uploaded MP4 takes priority, then YouTube iframe, then coming-soon.
const QuarterBlock = ({ quarter, index, refCallback }) => {
  const ytId = extractYouTubeId(quarter.youtube_url);
  const hasVideo = !!quarter.video_url;
  const flip = index % 2 === 1; // alternate layout for visual rhythm
  const [playing, setPlaying] = useState(false);

  return (
    <section
      ref={refCallback}
      data-quarter={quarter.quarter}
      data-testid={`gameplan-quarter-${quarter.quarter}`}
      className="relative px-6 md:px-12 py-24 md:py-36 border-b border-white/10"
    >
      <div className={`max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${flip ? "lg:[&>*:first-child]:order-2" : ""}`}>
        {/* Video */}
        <div className="lg:col-span-7">
          <Reveal>
            <div className="relative w-full aspect-video bg-black overflow-hidden ring-1 ring-white/10">
              {hasVideo ? (
                playing ? (
                  <video
                    data-testid={`gameplan-video-${quarter.quarter}`}
                    src={resolveImage(quarter.video_url)}
                    poster={quarter.poster_url ? resolveImage(quarter.poster_url) : undefined}
                    controls
                    autoPlay
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setPlaying(true)}
                    data-testid={`gameplan-play-${quarter.quarter}`}
                    className="absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-4 group cursor-pointer"
                  >
                    {quarter.poster_url ? (
                      <img
                        src={resolveImage(quarter.poster_url)}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 to-black" />
                    )}
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors" />
                    <div className="relative w-16 h-16 rounded-full border border-[#B8945A] flex items-center justify-center backdrop-blur-sm bg-black/40 group-hover:scale-110 transition-transform duration-300">
                      <Play size={22} className="text-[#B8945A] translate-x-[1px]" fill="currentColor" />
                    </div>
                    <p className="relative text-[10px] tracking-[0.32em] uppercase text-[#B8945A]">
                      Watch
                    </p>
                  </button>
                )
              ) : ytId ? (
                <iframe
                  data-testid={`gameplan-video-${quarter.quarter}`}
                  src={`https://www.youtube-nocookie.com/embed/${ytId}?rel=0&modestbranding=1`}
                  title={`${quarter.title} — Game Plan`}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-neutral-900 to-black">
                  {quarter.poster_url && (
                    <img
                      src={resolveImage(quarter.poster_url)}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover opacity-40"
                    />
                  )}
                  <div className="relative w-16 h-16 rounded-full border border-[#B8945A]/50 flex items-center justify-center backdrop-blur-sm bg-black/30">
                    <Play size={22} className="text-[#B8945A] translate-x-[1px]" fill="currentColor" />
                  </div>
                  <p className="relative text-[10px] tracking-[0.32em] uppercase text-[#B8945A]">
                    Coming soon
                  </p>
                </div>
              )}
            </div>
          </Reveal>
        </div>

        {/* Copy */}
        <div className="lg:col-span-5">
          <Reveal delay={120}>
            <p className="text-sm md:text-base tracking-[0.24em] uppercase text-white font-bold mb-6">
              {QUARTER_LABELS[quarter.quarter - 1]} · {quarter.subtitle}
            </p>
          </Reveal>
          <Reveal delay={180}>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-white">
              {quarter.title}
            </h2>
          </Reveal>
          <Reveal delay={260}>
            <p className="text-base md:text-lg text-white/65 mt-8 leading-relaxed max-w-xl">
              {quarter.description}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const GamePlan = () => {
  const [quarters, setQuarters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(1);
  const refs = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/game-plan");
        setQuarters(Array.isArray(data) ? data : []);
      } catch {
        setQuarters([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Update the sticky scoreboard as sections cross center.
  useEffect(() => {
    const onScroll = () => {
      const mid = window.innerHeight / 2;
      let bestQ = 1;
      let bestDelta = Infinity;
      for (const q of quarters) {
        const el = refs.current[q.quarter];
        if (!el) continue;
        const r = el.getBoundingClientRect();
        const center = r.top + r.height / 2;
        const delta = Math.abs(center - mid);
        if (delta < bestDelta) { bestDelta = delta; bestQ = q.quarter; }
      }
      setActive(bestQ);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [quarters]);

  const scrollToQuarter = (n) => {
    const el = refs.current[n];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main data-testid="gameplan-page" className="bg-black text-white min-h-screen">
      <Navigation alwaysDark />

      {/* Hero */}
      <section
        data-testid="gameplan-hero"
        className="relative pt-40 md:pt-48 pb-20 md:pb-32 px-6 md:px-12 overflow-hidden"
      >
        {/* Subtle basketball-court hardwood grain via SVG lines */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="hardwood" patternUnits="userSpaceOnUse" width="80" height="120">
                <rect width="80" height="120" fill="none"/>
                <path d="M0,0 L0,120 M40,0 L40,120 M80,0 L80,120" stroke="#B8945A" strokeWidth="0.5" opacity="0.4"/>
                <path d="M0,40 L80,40 M0,80 L80,80" stroke="#B8945A" strokeWidth="0.3" opacity="0.3"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hardwood)" />
          </svg>
        </div>

        <div className="relative max-w-[1400px] mx-auto">
          <Reveal>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-white/50 hover:text-[#B8945A] transition-colors mb-10"
            >
              ← Back home
            </Link>
          </Reveal>

          <Reveal delay={100}>
            <p className="text-[10px] tracking-[0.32em] uppercase mb-8 text-[#B8945A]">
              Campaign · Home-Court Advantage
            </p>
          </Reveal>

          <Reveal delay={160}>
            <h1 className="font-heading text-6xl md:text-7xl lg:text-8xl leading-[1.02] tracking-tight max-w-[10ch]">
              The Game Plan.
            </h1>
          </Reveal>

          <Reveal delay={220}>
            <p className="text-base md:text-xl text-white/60 mt-10 max-w-2xl leading-relaxed">
              Buying or selling a home is a four-quarter game. Coach Manzano walks you
              through every play — from the pregame prep to the final buzzer — so you
              step on the court knowing exactly how to win.
            </p>
          </Reveal>

          <Reveal delay={320}>
            <div className="mt-14 flex items-center gap-6">
              <button
                type="button"
                onClick={() => scrollToQuarter(1)}
                data-testid="gameplan-tipoff-cta"
                className="inline-flex items-center gap-3 px-10 py-4 bg-[#B8945A] text-black border border-[#B8945A] text-[11px] uppercase tracking-[0.28em] hover:bg-transparent hover:text-[#B8945A] transition-all duration-500"
              >
                Tip off
                <ChevronDown size={14} strokeWidth={2} />
              </button>
              <div className="hidden sm:flex items-center gap-3 text-[10px] tracking-[0.32em] uppercase text-white/40">
                Four videos · Roughly 20 minutes
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Sticky scoreboard */}
      {quarters.length > 0 && (
        <div
          data-testid="gameplan-scoreboard"
          className="sticky top-16 md:top-20 z-30 bg-black/85 backdrop-blur-md border-y border-white/10"
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between gap-4">
            <p className="text-[10px] tracking-[0.32em] uppercase text-white/40 hidden md:block">
              Scoreboard
            </p>
            <div className="flex items-center gap-2 md:gap-4 flex-1 md:flex-none justify-between md:justify-end">
              {quarters.map((q) => (
                <button
                  key={q.quarter}
                  onClick={() => scrollToQuarter(q.quarter)}
                  data-testid={`gameplan-jump-${q.quarter}`}
                  className={`group flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase transition-colors ${
                    active === q.quarter ? "text-[#B8945A]" : "text-white/50 hover:text-white"
                  }`}
                >
                  <span
                    className={`inline-block w-8 h-px transition-colors ${
                      active === q.quarter ? "bg-[#B8945A]" : "bg-white/20 group-hover:bg-white/40"
                    }`}
                  />
                  {QUARTER_LABELS[q.quarter - 1]}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quarters */}
      <div>
        {loading ? (
          <div className="px-6 md:px-12 py-32 text-center text-white/40">Loading…</div>
        ) : quarters.length === 0 ? (
          <div className="px-6 md:px-12 py-32 text-center text-white/50">
            <p className="overline mb-4 text-[#B8945A]">Game Plan</p>
            <p>No quarters published yet.</p>
          </div>
        ) : (
          quarters.map((q, i) => (
            <QuarterBlock
              key={q.id || q.quarter}
              quarter={q}
              index={i}
              refCallback={(el) => { refs.current[q.quarter] = el; }}
            />
          ))
        )}
      </div>

      {/* CTA */}
      <section className="px-6 md:px-12 py-28 md:py-36">
        <div className="max-w-[900px] mx-auto text-center">
          <Reveal>
            <p className="overline mb-6 text-[#B8945A]">Post-game</p>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Ready to run your play?
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-white/60 text-lg mt-8 max-w-xl mx-auto leading-relaxed">
              You&apos;ve watched the film. Now let&apos;s build your personal game
              plan — free consultation, zero pressure, straight talk.
            </p>
          </Reveal>
          <Reveal delay={280}>
            <Link
              to="/#contact"
              data-testid="gameplan-postgame-cta"
              className="inline-flex items-center justify-center px-12 py-4 mt-12 bg-[#B8945A] text-black border border-[#B8945A] text-[11px] uppercase tracking-[0.28em] hover:bg-transparent hover:text-[#B8945A] transition-all duration-500"
            >
              Book a call with Coach
            </Link>
          </Reveal>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default GamePlan;
